import { useNavigate } from 'react-router';
import { useState, MouseEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
//
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
//
import Iconify from '@/components/Iconify';
import Label from '@/components/Label';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import AddressWithGoogleMapsIconButton from '@/components/AddressWithGoogleMapsIconButton';
import { getFilteredBookingSession } from '@/features/bookings-management/api';
// utils
import { fCurrency } from '@/utils/formatNumber';
import { fDate } from '@/utils/formatTime';
import { providersPath } from '@/features/users/providers/routes/paths';
import { bookingsManagementPath } from '@/features/bookings-management/routes/paths';
import { returnSessionFilterFromDate } from '@/features/bookings-management/utils/bookingSessions';
import {
  CLIENT_BOOKINGS_TABLE_COLUMN_COUNT,
  getClientBookingSessionCounts,
  getPreferredClientBookingSessionFilter,
} from '@/features/users/clients/utils';
import type { ClientBookingSessionDetailFilter } from '@/features/users/clients/utils';
// types
import { LabelColors } from '@/types';
import type {
  Booking,
  BookingSession,
} from '@/features/bookings-management/types';

type ClientBookingsTableRowProps = {
  row: Booking;
  canDelete: boolean;
  canUpdate: boolean;
};

const SESSION_PREVIEW_FILTERS: {
  value: ClientBookingSessionDetailFilter;
  label: string;
}[] = [
  { value: 'all', label: 'All' },
  { value: 'today', label: 'Today' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'previous', label: 'Previous' },
];

const SESSION_PREVIEW_FETCH_FILTERS = ['today', 'upcoming', 'previous'] as const;

const getFilterLabel = (filter: ClientBookingSessionDetailFilter) =>
  SESSION_PREVIEW_FILTERS.find((option) => option.value === filter)?.label ??
  filter;

const getStatusLabel = (
  status?: BookingSession['booking_session_status'] | null
) => status?.display ?? status?.value ?? 'N/A';

const getStatusColor = (
  status?: BookingSession['booking_session_status'] | null
) => (status?.style_class as LabelColors) ?? 'info';

const getSessionCleanerLabel = (session: BookingSession) => {
  const cleaners = session.booking_session_cleaners ?? [];

  if (cleaners.length === 0) return 'Unassigned';

  return cleaners
    .map((cleaner) =>
      `${cleaner?.first_name ?? 'N/A'} ${cleaner?.last_name ?? 'N/A'}`.trim()
    )
    .join(', ');
};

export default function ClientBookingsTableRow({
  row,
  canDelete,
  canUpdate,
}: ClientBookingsTableRowProps) {
  const navigate = useNavigate();

  const {
    id,
    date,
    time,
    total_amount,
    service_variant,
    address,
    booking_cleaners,
    booking_status,
  } = row;
  const sessionCounts = getClientBookingSessionCounts(row);
  const preferredSessionFilter = getPreferredClientBookingSessionFilter(row);
  const bookingSessionsLink = bookingsManagementPath('view', {
    bookingId: String(id),
    bookingTabSid: 'booking-sessions',
    sessionFilter: preferredSessionFilter,
  });

  const cleaner =
    booking_cleaners?.[0]?.first_name && booking_cleaners?.[0]?.last_name
      ? `${booking_cleaners?.[0]?.first_name ?? 'N/A'} ${
          booking_cleaners?.[0]?.last_name ?? 'N/A'
        }`
      : null;

  const cleanerId = !!booking_cleaners?.length ? booking_cleaners[0]?.id : 0;

  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedSessionFilter, setSelectedSessionFilter] =
    useState<ClientBookingSessionDetailFilter>('all');

  const {
    data: previewSessions = [],
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = useQuery({
    queryKey: ['clientBookingSessions', id, selectedSessionFilter],
    queryFn: async () => {
      if (selectedSessionFilter !== 'all') {
        return getFilteredBookingSession(id, selectedSessionFilter);
      }

      const sessionsByFilter = await Promise.all(
        SESSION_PREVIEW_FETCH_FILTERS.map((filter) =>
          getFilteredBookingSession(id, filter)
        )
      );
      const sessionsById = new Map<number, BookingSession>();

      sessionsByFilter.flat().forEach((session) => {
        sessionsById.set(session.id, session);
      });

      return Array.from(sessionsById.values());
    },
    enabled: isPreviewOpen,
    refetchOnWindowFocus: false,
  });

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleTogglePreview = () => {
    if (!isPreviewOpen) {
      setSelectedSessionFilter('all');
    }

    setIsPreviewOpen((current) => !current);
  };

  const handleSelectSessionFilter = (
    filter: ClientBookingSessionDetailFilter
  ) => {
    const count =
      filter === 'all'
        ? sessionCounts.total
        : filter === 'previous'
        ? sessionCounts.previous
        : sessionCounts[filter];

    if (count === 0) return;

    setSelectedSessionFilter(filter);
    setIsPreviewOpen(true);
  };

  const getSessionDetailFilter = (session: BookingSession) => {
    if (selectedSessionFilter !== 'all') return selectedSessionFilter;

    const filterFromDate = returnSessionFilterFromDate(session.date);
    return filterFromDate === 'error' ? preferredSessionFilter : filterFromDate;
  };

  const getSessionLink = (session: BookingSession) => {
    const sessionFilter = getSessionDetailFilter(session);

    return `${bookingsManagementPath('view', {
      bookingId: String(session.booking_id ?? id),
      bookingTabSid: 'booking-sessions',
      sessionFilter,
    })}?sessionID=${session.id}`;
  };

  const renderSessionCountChip = (
    filter: ClientBookingSessionDetailFilter,
    label: string
  ) => {
    const count =
      filter === 'all'
        ? sessionCounts.total
        : filter === 'previous'
        ? sessionCounts.previous
        : sessionCounts[filter];
    const isSelected = isPreviewOpen && selectedSessionFilter === filter;

    return (
      <Chip
        key={filter}
        label={`${label} ${count}`}
        size="small"
        color={isSelected ? 'primary' : 'default'}
        variant={isSelected ? 'filled' : 'outlined'}
        disabled={count === 0}
        clickable={count > 0}
        onClick={() => handleSelectSessionFilter(filter)}
      />
    );
  };

  return (
    <>
      <TableRow hover>
        <TableCell width={48}>
          <Tooltip
            title={
              isPreviewOpen
                ? 'Hide booking sessions'
                : 'Show booking sessions'
            }
          >
            <IconButton
              aria-label={
                isPreviewOpen
                  ? `Hide sessions for booking ${id}`
                  : `Show sessions for booking ${id}`
              }
              size="small"
              onClick={handleTogglePreview}
            >
              <Iconify
                icon={
                  isPreviewOpen
                    ? 'eva:arrow-ios-upward-fill'
                    : 'eva:arrow-ios-downward-fill'
                }
                width={18}
                height={18}
              />
            </IconButton>
          </Tooltip>
        </TableCell>
        {/* ID */}
        <TableCell align="left">
          <TableRowLink
            linkText={id}
            linkTo={bookingSessionsLink}
          />
        </TableCell>

        {/* DATE AND TIME */}
        <TableCell align="left">
          <Typography noWrap fontSize={14}>
            {time ? time.slice(0, 5) : 'N/A'} @{' '}
            {date ? fDate(date, 'EE, dd MMM yy') : 'N/A'}
          </Typography>
        </TableCell>

        {/* TOTAL AMOUNT */}
        <TableCell align="left">{fCurrency(total_amount)}</TableCell>

        {/* BOOKING SERVICE */}
        <TableCell align="left">
          {service_variant?.service?.name ?? 'N/A'}
        </TableCell>

        {/* SERVICE VARIANT */}
        <TableCell align="left">{service_variant?.name ?? 'N/A'}</TableCell>

        {/* SESSIONS */}
        <TableCell align="left">
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
            {SESSION_PREVIEW_FILTERS.map((filter) =>
              renderSessionCountChip(filter.value, filter.label)
            )}
          </Stack>
        </TableCell>

        {/* ADDRESS */}
        <TableCell align="left">
          <AddressWithGoogleMapsIconButton
            formatted_address={address.formatted_address}
            postcode={address.postcode}
          />
        </TableCell>

        {/* BOOKING STATUS */}
        <TableCell align="left">
          <Label color={(booking_status?.style_class as LabelColors) ?? 'info'}>
            {booking_status?.display ?? 'N/A'}
          </Label>
        </TableCell>

        <TableCell align="left">
          {cleaner ? (
            <TableRowLink
              linkText={cleaner}
              linkTo={providersPath('edit', {
                userId: String(cleanerId),
                tabSid: 'details',
              })}
              noWrap
            />
          ) : (
            <Typography
              fontStyle="italic"
              fontSize={14}
              fontWeight={500}
              sx={{ pl: 1 }}
            >
              Unassigned
            </Typography>
          )}
        </TableCell>

        <TableCell align="right">
          {(canDelete || canUpdate) && (
            <TableMoreMenu
              open={openMenu}
              onOpen={handleOpenMenu}
              onClose={handleCloseMenu}
              actions={
                <>
                  {canUpdate && (
                    <MenuItem
                      onClick={() =>
                        navigate(bookingSessionsLink)
                      }
                    >
                      <Iconify icon={'eva:edit-fill'} />
                      Edit
                    </MenuItem>
                  )}
                  {canDelete && (
                    <MenuItem
                      onClick={() => alert('DELETE')}
                      sx={{ color: 'error.main' }}
                    >
                      <Iconify icon={'eva:trash-2-outline'} />
                      Delete
                    </MenuItem>
                  )}
                </>
              }
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          colSpan={CLIENT_BOOKINGS_TABLE_COLUMN_COUNT}
          sx={{ py: 0, borderBottom: isPreviewOpen ? undefined : 0 }}
        >
          <Collapse in={isPreviewOpen} timeout="auto" unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 2,
                bgcolor: 'background.neutral',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 1.5 }}
              >
                <Typography variant="subtitle2">
                  {getFilterLabel(selectedSessionFilter)} sessions
                </Typography>
                <TableRowLink
                  linkText="Open full session view"
                  linkTo={bookingsManagementPath('view', {
                    bookingId: String(id),
                    bookingTabSid: 'booking-sessions',
                    sessionFilter:
                      selectedSessionFilter === 'all'
                        ? preferredSessionFilter
                        : selectedSessionFilter,
                  })}
                />
              </Stack>

              {isPreviewLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading sessions...
                  </Typography>
                </Stack>
              ) : isPreviewError ? (
                <Typography variant="body2" color="error">
                  Unable to load sessions.
                </Typography>
              ) : previewSessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No {getFilterLabel(selectedSessionFilter).toLowerCase()}{' '}
                  sessions found.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Session</TableCell>
                      <TableCell>Date & time</TableCell>
                      <TableCell>Cleaner</TableCell>
                      <TableCell>Session status</TableCell>
                      <TableCell>Payment status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewSessions.map((session) => (
                      <TableRow key={session.id}>
                        <TableCell>
                          <TableRowLink
                            linkText={`#${session.id}`}
                            linkTo={getSessionLink(session)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography noWrap fontSize={13}>
                            {session.time ? session.time.slice(0, 5) : 'N/A'} @{' '}
                            {session.date
                              ? fDate(session.date, 'EE, dd MMM yy')
                              : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            fontSize={13}
                            fontStyle={
                              getSessionCleanerLabel(session) === 'Unassigned'
                                ? 'italic'
                                : 'normal'
                            }
                          >
                            {getSessionCleanerLabel(session)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Label
                            color={getStatusColor(
                              session.booking_session_status
                            )}
                          >
                            {getStatusLabel(session.booking_session_status)}
                          </Label>
                        </TableCell>
                        <TableCell>
                          <Label
                            color={getStatusColor(
                              session.booking_session_payment_status
                            )}
                          >
                            {getStatusLabel(
                              session.booking_session_payment_status
                            )}
                          </Label>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
